import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import casinoService from '../services/casino.service'
import {
  LOTUS_CASINO_GC_MS,
  LOTUS_CASINO_STALE_MS,
  lotusCasinoKeys,
} from '../lib/lotusCasinoQueries'
import type { CasinoGameRecord } from '../utils/lotusCasinoGameFilter'

export type LotusProviderCategory = { _id: string }

const DEFAULT_VIS = {
  disabledProviders: [] as string[],
  disabledGames: [] as string[],
  overrideImages: {} as Record<string, string>,
  useCustomImage: {} as Record<string, boolean>,
}

type ProviderPageData = {
  games: CasinoGameRecord[]
  categorylist: LotusProviderCategory[]
}

async function fetchVisibilityConfig(preLoginMode: boolean) {
  const configRequest = preLoginMode
    ? casinoService.getPublicCasinoVisibilityConfig().catch(() => casinoService.getCasinoVisibilityConfig())
    : casinoService.getCasinoVisibilityConfig()

  try {
    const res = (await configRequest) as AxiosResponse<
      { data?: typeof DEFAULT_VIS } & typeof DEFAULT_VIS
    >
    const data = res.data?.data || res.data || {}
    return {
      disabledProviders: data.disabledProviders || [],
      disabledGames: data.disabledGames || [],
      overrideImages: data.overrideImages || {},
      useCustomImage: data.useCustomImage || {},
    }
  } catch {
    return DEFAULT_VIS
  }
}

async function fetchProviderPageData(
  category: string,
  casinoType: string,
  preLoginMode: boolean,
): Promise<ProviderPageData> {
  const res = preLoginMode
    ? await casinoService.getPublicIntCasinoList(category, casinoType)
    : await casinoService.getIntCasinoList(category, casinoType)
  const payload = (res as AxiosResponse<{ result?: CasinoGameRecord[]; category?: LotusProviderCategory[] }>).data
  return {
    games: Array.isArray(payload?.result) ? payload.result : [],
    categorylist: Array.isArray(payload?.category) ? payload.category : [],
  }
}

export function useLotusProviderPageData(
  category: string | null,
  casinoType = 'slots',
  options: { preLoginMode?: boolean } = {},
) {
  const { preLoginMode = false } = options
  const queryClient = useQueryClient()

  const visibilityQuery = useQuery({
    queryKey: lotusCasinoKeys.visibility(preLoginMode),
    queryFn: () => fetchVisibilityConfig(preLoginMode),
    staleTime: LOTUS_CASINO_STALE_MS,
    gcTime: LOTUS_CASINO_GC_MS,
  })

  const pageQuery = useQuery({
    queryKey: category
      ? [...lotusCasinoKeys.all, 'providerPage', category, casinoType, preLoginMode ? 'public' : 'auth']
      : [...lotusCasinoKeys.all, 'providerPage', 'empty'],
    queryFn: async () => {
      const data = await fetchProviderPageData(category!, casinoType, preLoginMode)
      queryClient.setQueryData(
        lotusCasinoKeys.providerList(category!, casinoType, preLoginMode),
        data.games,
      )
      return data
    },
    enabled: Boolean(category),
    staleTime: LOTUS_CASINO_STALE_MS,
    gcTime: LOTUS_CASINO_GC_MS,
    initialData: () => {
      if (!category) return undefined
      const cachedGames = queryClient.getQueryData<CasinoGameRecord[]>(
        lotusCasinoKeys.providerList(category, casinoType, preLoginMode),
      )
      if (!cachedGames) return undefined
      return { games: cachedGames, categorylist: [] as LotusProviderCategory[] }
    },
  })

  const games = pageQuery.data?.games ?? []
  const categorylist = pageQuery.data?.categorylist ?? []
  const loading = Boolean(category) && pageQuery.isLoading && !pageQuery.data

  return {
    games,
    categorylist,
    loading,
    visibilityConfig: visibilityQuery.data ?? DEFAULT_VIS,
  }
}

export function filterProviderGames(
  games: CasinoGameRecord[],
  catName: string,
  visibilityConfig: typeof DEFAULT_VIS,
): CasinoGameRecord[] {
  let list = games

  if (visibilityConfig.disabledProviders.length) {
    list = list.filter((g) => {
      const pid = g.provider_id || g.provider || g.game_provider
      return !pid || !visibilityConfig.disabledProviders.includes(String(pid))
    })
  }

  if (visibilityConfig.disabledGames.length) {
    list = list.filter((g) => {
      const gid = g.game_identifier || g.identifier || g._id
      return !gid || !visibilityConfig.disabledGames.includes(String(gid))
    })
  }

  if (!catName || catName === 'All') return list

  return list.filter((game) => {
    const sub =
      game.game_category || game.live_category || game.slot_category
    return sub === catName
  })
}

export function formatProviderDisplayName(category: string): string {
  if (category === 'Evolution Live') return 'Evolution'
  if (category === 'Mac88 Gaming') return 'Mac88'
  if (category === '100hp') return '100HP'
  return category
}
